<template>
  <article class="instagram-flyer-card" :class="{ 'instagram-flyer-card-masonry': view === 'masonry' }">
    <header class="instagram-flyer-header">
      <router-link class="instagram-author" :to="authorRoute">
        <img class="instagram-avatar" :src="authorAvatar" :alt="authorName" loading="lazy" decoding="async" />
        <span class="min-w-0">
          <strong class="instagram-author-name block truncate text-sm font-semibold capitalize text-slate-950">{{ authorName }}</strong>
          <span v-if="locationLabel" class="instagram-location mt-0.5 flex min-w-0 items-center gap-1 text-xs text-slate-500">
            <i class="ri-map-pin-2-line shrink-0 text-blue-600" aria-hidden="true"></i>
            <span class="truncate">{{ locationLabel }}</span>
          </span>
        </span>
      </router-link>

      <div class="relative">
        <button class="instagram-icon-button" type="button" aria-label="More flyer actions" @click="menuOpen = !menuOpen">
          <i class="ri-more-fill text-xl" aria-hidden="true"></i>
        </button>
        <div v-if="menuOpen" class="instagram-menu">
          <button class="instagram-menu-item" type="button" @click="openShare">
            <i class="ri-share-forward-line" aria-hidden="true"></i><span>Share</span>
          </button>
        </div>
      </div>
    </header>

    <router-link :to="detailRoute" class="block bg-slate-100" aria-label="Open flyer details">
      <FlyerImageCarousel v-if="imageSources.length" :images="imageSources" variant="instagram" />
      <div v-else class="instagram-text-post" :style="{ background: fallbackBackground }">
        <p>{{ caption || flyer.title || categoryLabel }}</p>
      </div>
    </router-link>

    <div class="instagram-actions">
      <div class="flex items-center gap-1">
        <button
          class="instagram-action-button"
          :class="{ 'text-rose-500': liked }"
          type="button"
          :disabled="likeSaving || isOwnFlyer"
          :aria-label="isOwnFlyer ? 'Like unavailable for your flyer' : (liked ? `Unlike flyer, ${likesCount} likes` : `Like flyer, ${likesCount} likes`)"
          :aria-pressed="liked"
          @click="toggleLike"
        >
          <i :class="liked ? 'ri-heart-fill' : 'ri-heart-line'" aria-hidden="true"></i>
          <span>{{ likesCount }}</span>
        </button>
        <router-link class="instagram-action-button" :to="detailRoute" :aria-label="`View ${commentsCount} comments`">
          <i class="ri-chat-3-line" aria-hidden="true"></i>
          <span>{{ commentsCount }}</span>
        </router-link>
        <button class="instagram-icon-button" type="button" aria-label="Share flyer" @click="openShare">
          <i class="ri-send-plane-line" aria-hidden="true"></i>
        </button>
      </div>

      <button
        v-if="!isOwnFlyer"
        class="instagram-icon-button"
        :class="{ 'text-blue-600': saved }"
        type="button"
        :aria-label="saved ? 'Manage saved flyer' : 'Save flyer'"
        :aria-pressed="saved"
        @click="openSaveBoard"
      >
        <i :class="saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'" aria-hidden="true"></i>
      </button>
    </div>

    <div class="instagram-body">
      <p v-if="caption" class="instagram-caption m-0 text-sm leading-5 text-slate-700">
        <router-link class="mr-1 font-semibold text-slate-950 hover:underline" :to="authorRoute">{{ authorName }}</router-link>
        <span>{{ visibleCaption }}</span>
        <button v-if="captionIsLong" class="ml-1 text-slate-500 hover:text-slate-800" type="button" @click="captionExpanded = !captionExpanded">
          {{ captionExpanded ? 'less' : 'more' }}
        </button>
      </p>
      <router-link v-if="commentsCount" class="instagram-comments-link w-fit text-sm text-slate-500 hover:text-slate-800" :to="detailRoute">
        View all {{ commentsCount }} comments
      </router-link>
      <span v-if="relativeDateLabel" class="instagram-time text-[0.68rem] font-medium uppercase tracking-wide text-slate-400">{{ relativeDateLabel }}</span>
    </div>

    <Teleport to="body">
      <ShareFlyerModal v-if="shareModalOpen" :flyer="flyer" @close="shareModalOpen = false" />
      <SaveToBoardModal
        v-if="saveBoardModalOpen"
        :flyer-id="flyer.id"
        :initial-saved="saved"
        :initial-board-id="savedBoardId"
        @close="saveBoardModalOpen = false"
        @saved-change="handleSavedChange"
      />
    </Teleport>
  </article>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FlyerImageCarousel from '@/components/common/FlyerImageCarousel.vue'
import SaveToBoardModal from '@/components/common/SaveToBoardModal.vue'
import ShareFlyerModal from '@/components/common/ShareFlyerModal.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'

const props = defineProps({
  flyer: { type: Object, required: true },
  view: { type: String, default: 'post' }
})
const router = useRouter()
const route = useRoute()
const session = useSessionStore()
const menuOpen = ref(false)
const shareModalOpen = ref(false)
const saveBoardModalOpen = ref(false)
const captionExpanded = ref(false)
const likeSaving = ref(false)
const commentsCount = ref(0)
let commentsRequestId = 0
const normalizeLiked = (flyer = {}) => {
  const status = [flyer.liked, flyer.is_liked, flyer.like_status, flyer.liked_status]
    .find((value) => value !== undefined && value !== null && value !== '')
  return ['1', 'true', 'yes', 'liked'].includes(String(status || '').trim().toLowerCase())
}
const liked = ref(normalizeLiked(props.flyer))
const saved = ref(normalizeFlyerSaved(props.flyer))
const savedBoardId = ref(String(getFlyerBoardId(props.flyer) || '').trim())
const localLikesCount = ref(Number(props.flyer.likesCount || props.flyer.likes_count || props.flyer.like_count || 0))

const decodeHtmlEntities = (value = '') => {
  let decoded = String(value || '')

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = decoded
      decoded = textarea.value
    } else {
      decoded = decoded
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#0?39;/gi, "'")
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
    }
    if (decoded === previous) break
  }

  return decoded
}

const cleanText = (value = '') => decodeHtmlEntities(value)
  .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<\/p\s*>/gi, ' ')
  .replace(/<\/div\s*>/gi, ' ')
  .replace(/<li\b[^>]*>/gi, ' • ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const currentUserId = computed(() => String(session.userId || session.user?.user_id || session.user?.id || '').trim())
const ownerId = computed(() => String(props.flyer.userId || props.flyer.user_id || props.flyer.userid || props.flyer.uid || props.flyer.created_by || '').trim())
const isOwnFlyer = computed(() => Boolean(currentUserId.value && ownerId.value && currentUserId.value === ownerId.value))
const authorName = computed(() => String(props.flyer.author || props.flyer.user_name || props.flyer.username || 'NetFlyr').trim())
const authorRoute = computed(() => isOwnFlyer.value ? '/account' : `/users/${ownerId.value || props.flyer.id}`)
const authorAvatar = computed(() => props.flyer.authorAvatar || props.flyer.user_img || `https://dummyimage.com/96x96/e0e7ff/1d4ed8&text=${encodeURIComponent(authorName.value.charAt(0) || 'N')}`)
const locationLabel = computed(() => String(props.flyer.location || props.flyer.city || props.flyer.address || '').trim())
const detailRoute = computed(() => `/flyers/${props.flyer.id}`)
const categoryLabel = computed(() => String(props.flyer.category || props.flyer.category_name || 'Flyer').trim())
const caption = computed(() => cleanText(props.flyer.description || props.flyer.title || '')
  .replace(/(^|\s)#[\p{L}\p{N}_-]+/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim())
const captionIsLong = computed(() => caption.value.length > 180)
const visibleCaption = computed(() => captionIsLong.value && !captionExpanded.value ? `${caption.value.slice(0, 180).trimEnd()}…` : caption.value)
const relativeDateLabel = computed(() => props.flyer.time_ago || props.flyer.timeAgo || props.flyer.postedAt || props.flyer.created_at || '')
const likesCount = computed(() => Math.max(0, Number(localLikesCount.value) || 0))
const imageSources = computed(() => Array.from(new Set([
  ...(Array.isArray(props.flyer.images) ? props.flyer.images : []),
  props.flyer.image,
  props.flyer.image1,
  props.flyer.image2,
  props.flyer.image3,
  props.flyer.image4
].filter(Boolean))))
const fallbackBackground = computed(() => {
  const raw = String(props.flyer.param_code || props.flyer.code || props.flyer.categoryColor || '2563eb').replace('#', '')
  return `#${/^[0-9a-f]{6}$/i.test(raw) ? raw : '2563eb'}`
})

const requireLogin = () => {
  if (session.isLoggedIn && currentUserId.value) return true
  router.push({ path: '/login', query: { redirect: route.fullPath } })
  return false
}
const openShare = () => { menuOpen.value = false; shareModalOpen.value = true }
const openSaveBoard = () => {
  if (!requireLogin()) return
  saveBoardModalOpen.value = true
}
const handleSavedChange = ({ saved: nextSaved, boardIds = [] } = {}) => {
  saved.value = Boolean(nextSaved)
  savedBoardId.value = saved.value ? boardIds.map(String).find(Boolean) || savedBoardId.value : ''
}

const extractComments = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.data,
    payload.list,
    payload.result,
    payload.comments,
    Array.isArray(payload.data) ? payload.data : payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.comments
  ]

  return candidates.find((candidate) => Array.isArray(candidate)) || []
}

const fetchCommentsCount = async (flyerId = props.flyer.id) => {
  const normalizedFlyerId = String(flyerId || '').trim()
  const requestId = ++commentsRequestId
  commentsCount.value = 0
  if (!normalizedFlyerId) return

  try {
    const { data } = await api.viewCommentsFlyer({ flyer_id: normalizedFlyerId })
    if (requestId !== commentsRequestId) return
    commentsCount.value = extractComments(data).length
  } catch {
    if (requestId === commentsRequestId) commentsCount.value = 0
  }
}

const toggleLike = async () => {
  if (!props.flyer.id || likeSaving.value || isOwnFlyer.value || !requireLogin()) return
  const previousLiked = liked.value
  const previousCount = likesCount.value
  liked.value = !liked.value
  localLikesCount.value = Math.max(0, previousCount + (liked.value ? 1 : -1))
  likeSaving.value = true
  try {
    await api.likeFlyer({ flyer_id: props.flyer.id, user_id: currentUserId.value })
  } catch {
    liked.value = previousLiked
    localLikesCount.value = previousCount
  } finally {
    likeSaving.value = false
  }
}

watch(() => props.flyer, (flyer) => {
  liked.value = normalizeLiked(flyer)
  saved.value = normalizeFlyerSaved(flyer)
  savedBoardId.value = String(getFlyerBoardId(flyer) || '').trim()
  localLikesCount.value = Number(flyer.likesCount || flyer.likes_count || flyer.like_count || 0)
  captionExpanded.value = false
}, { deep: true })

watch(() => props.flyer.id, fetchCommentsCount, { immediate: true })
</script>

<style scoped>
.instagram-flyer-card { overflow: hidden; border: 1px solid rgb(219 219 219); border-radius: 0.75rem; background: white; box-shadow: 0 1px 2px rgb(15 23 42 / 0.04); }
.instagram-flyer-header { display: flex; min-height: 3.75rem; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.65rem 0.75rem; }
.instagram-author { display: flex; min-width: 0; flex: 1; align-items: center; gap: 0.7rem; }
.instagram-avatar { width: 2.25rem; height: 2.25rem; flex: none; border: 2px solid white; border-radius: 9999px; object-fit: cover; box-shadow: 0 0 0 2px #d946ef, 0 0 0 3px #f97316; }
.instagram-actions { display: flex; align-items: center; justify-content: space-between; padding: 0.45rem 0.55rem 0.1rem; }
.instagram-icon-button { display: inline-grid; width: 2.35rem; height: 2.35rem; place-items: center; border-radius: 9999px; color: #0f172a; transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease; }
.instagram-icon-button:hover { background: #f1f5f9; transform: scale(1.04); }
.instagram-icon-button i { font-size: 1.55rem; line-height: 1; }
.instagram-action-button { display: inline-flex; min-width: 2.35rem; height: 2.35rem; align-items: center; justify-content: center; gap: 0.2rem; border-radius: 9999px; padding: 0 0.45rem; color: #0f172a; font-size: 0.78rem; font-weight: 600; transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease; }
.instagram-action-button:hover { background: #f1f5f9; transform: scale(1.04); }
.instagram-action-button:disabled { cursor: not-allowed; opacity: 0.48; transform: none; }
.instagram-action-button.text-rose-500 { color: #f43f5e; }
.instagram-action-button i { font-size: 1.35rem; font-weight: 400; line-height: 1; }
.instagram-menu { position: absolute; right: 0; z-index: 20; display: grid; min-width: 8rem; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 0.75rem; background: white; padding: 0.25rem; box-shadow: 0 14px 35px rgb(15 23 42 / 0.16); }
.instagram-menu-item { display: flex; min-height: 2.35rem; align-items: center; gap: 0.55rem; border-radius: 0.55rem; padding: 0 0.7rem; font-size: 0.82rem; font-weight: 500; }
.instagram-menu-item:hover { background: #f8fafc; }
.instagram-text-post { display: grid; place-items: center; padding: clamp(1.5rem, 6vw, 3.5rem) 2rem; color: white; }
.instagram-text-post p { margin: 0; max-width: 26rem; text-align: center; font-size: clamp(1.25rem, 4vw, 2rem); font-weight: 600; line-height: 1.35; }
.instagram-body { display: grid; gap: 0.35rem; padding: 0.2rem 0.8rem 0.9rem; }
.instagram-flyer-card-masonry .instagram-flyer-header { min-height: 3.25rem; padding: 0.5rem 0.6rem; }
.instagram-flyer-card-masonry .instagram-avatar { width: 1.9rem; height: 1.9rem; }
.instagram-flyer-card-masonry .instagram-author { gap: 0.45rem; }
.instagram-flyer-card-masonry .instagram-actions { padding: 0.3rem 0.2rem 0; }
.instagram-flyer-card-masonry .instagram-actions > div { gap: 0; }
.instagram-flyer-card-masonry .instagram-icon-button { width: 2rem; height: 2rem; }
.instagram-flyer-card-masonry .instagram-icon-button i { font-size: 1.25rem; }
.instagram-flyer-card-masonry .instagram-action-button { min-width: 2rem; height: 2rem; padding-inline: 0.3rem; font-size: 0.7rem; }
.instagram-flyer-card-masonry .instagram-action-button i { font-size: 1.15rem; font-weight: 400; }
.instagram-flyer-card-masonry .instagram-body { gap: 0.25rem; padding: 0.15rem 0.65rem 0.7rem; }
.instagram-flyer-card-masonry .instagram-text-post { padding: 1rem 0.65rem; }
.instagram-flyer-card-masonry .instagram-text-post p { font-size: 0.9rem; line-height: 1.4; }
@media (max-width: 640px) {
  .instagram-flyer-card:not(.instagram-flyer-card-masonry) { border-inline: 0; border-radius: 0; }
  .instagram-flyer-card-masonry { border: 1px solid rgb(219 219 219); border-radius: 0.65rem; }
  .instagram-flyer-card-masonry .instagram-flyer-header { min-height: 2.8rem; padding: 0.4rem; }
  .instagram-flyer-card-masonry .instagram-avatar { width: 1.65rem; height: 1.65rem; }
  .instagram-flyer-card-masonry .instagram-icon-button { width: 1.7rem; height: 1.7rem; }
  .instagram-flyer-card-masonry .instagram-icon-button i { font-size: 1.05rem; }
  .instagram-flyer-card-masonry .instagram-action-button { min-width: 1.7rem; height: 1.7rem; padding-inline: 0.2rem; font-size: 0.65rem; }
  .instagram-flyer-card-masonry .instagram-action-button i { font-size: 1rem; }
  .instagram-flyer-card-masonry .instagram-body { padding: 0.1rem 0.5rem 0.55rem; }
}

@media (max-width: 1279px) {
  .instagram-author-name { font-size: 0.82rem; }
  .instagram-location { font-size: 0.7rem; }
  .instagram-caption { font-size: 0.8rem; line-height: 1.25rem; }
  .instagram-comments-link { font-size: 0.78rem; }
  .instagram-time { font-size: 0.62rem; }
  .instagram-menu-item { min-height: 2.15rem; font-size: 0.76rem; }
  .instagram-text-post p { font-size: clamp(1rem, 3.2vw, 1.5rem); }
}

@media (max-width: 767px) {
  .instagram-author-name { font-size: 0.78rem; }
  .instagram-location { font-size: 0.66rem; }
  .instagram-caption { font-size: 0.76rem; line-height: 1.15rem; }
  .instagram-comments-link { font-size: 0.74rem; }
  .instagram-time { font-size: 0.58rem; }
  .instagram-flyer-card-masonry .instagram-author-name { font-size: 0.7rem; }
  .instagram-flyer-card-masonry .instagram-location { font-size: 0.6rem; }
  .instagram-flyer-card-masonry .instagram-caption { font-size: 0.7rem; line-height: 1.05rem; }
  .instagram-flyer-card-masonry .instagram-comments-link { font-size: 0.68rem; }
  .instagram-flyer-card-masonry .instagram-time { font-size: 0.55rem; letter-spacing: 0.02em; }
  .instagram-flyer-card-masonry .instagram-text-post p { font-size: 0.82rem; line-height: 1.3; }
}
</style>
